import { make } from 'vuex-pathify'
import axios from 'axios'
import { getQueryFromFilter, showError, showSuccess } from '@/util/helpers'

const state = {
  document: {
    items: [],
  },

  documents: {
    data: [],
    count: 0,
    limit: 50,
  },

  filter: {
    page: 1,
    itemsPerPage: 25,
    sortBy: ['createdAt'],
    sortDesc: [true],
  },

  loading: false,
  dialog: false,
}

const getCurrentCompanyQuery = () => getQueryFromFilter({})

const mutations = make.mutations(state)

const actions = {
  view: async ({ state, dispatch }, id) => {
    state.loading = true
    try {
      const res = await dispatch('getById', id)
      if (!res.data) {
        showError('Documento fiscal não encontrado')
        return
      }

      state.document = res.data
      state.dialog = true
    } catch (e) {
      showError(e)
    } finally {
      state.loading = false
    }
  },

  get: async ({ state }, filter = state.filter) => {
    state.loading = true
    return axios.get(`${process.env.VUE_APP_API_URL}/fiscal?${getQueryFromFilter(filter)}`)
      .then(res => {
        state.documents = res.data
        return res.data
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  consult: async ({ state }, notaFiscalKey) => {
    state.loading = true
    try {
      const res = await axios.get(`${process.env.VUE_APP_API_URL}/fiscal/nfse/get/${notaFiscalKey}?${getCurrentCompanyQuery()}`)
      if (!res.data) {
        showError('Documento fiscal não encontrado')
        return
      }

      state.document = res.data
      state.dialog = true
    } catch (e) {
      showError(e)
    } finally {
      state.loading = false
    }
  },

  nfseHealth: async ({ state }) => {
    state.loading = true
    return axios.get(`${process.env.VUE_APP_API_URL}/fiscal/nfse/health?${getCurrentCompanyQuery()}`)
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  previewAccountantDocuments: async (_context, period) => {
    const response = await axios.get(`${process.env.VUE_APP_API_URL}/fiscal/accountant-documents?${getQueryFromFilter(period)}`)
    return response.data
  },

  sendAccountantDocuments: async (_context, payload) => {
    const response = await axios.post(`${process.env.VUE_APP_API_URL}/fiscal/accountant-documents?${getCurrentCompanyQuery()}`, payload)
    return response.data
  },

  sendEmail: async ({ state }, id) => {
    state.loading = true
    return axios.post(`${process.env.VUE_APP_API_URL}/fiscal/${id}/email?${getCurrentCompanyQuery()}`)
      .then(() => showSuccess('Envio de email enfileirado com sucesso!'))
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  sendEmailBulk: async ({ state }, ids) => {
    state.loading = true
    return axios.post(`${process.env.VUE_APP_API_URL}/fiscal/email/bulk?${getCurrentCompanyQuery()}`, { ids })
      .then(res => {
        const queued = Number(res?.data?.queued || 0)
        const ignored = Array.isArray(res?.data?.ignored) ? res.data.ignored : []

        if (ignored.length) {
          showError(ignored.map(item => `Documento ${item.id}: ${item.reason}`))
        }

        if (queued > 0) {
          showSuccess(`${queued} envio(s) enfileirado(s) com sucesso!`)
        }

        return res.data
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  cancelNfe: async ({ state, dispatch }, { id, justification }) => {
    state.loading = true
    return axios.post(`${process.env.VUE_APP_API_URL}/fiscal/${id}/cancel?${getCurrentCompanyQuery()}`, { justification })
      .then(res => {
        showSuccess('Cancelamento da NFe enfileirado com sucesso!')
        dispatch('get')
        return res.data
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  correctionLetter: async ({ state, dispatch }, { id, correctionText }) => {
    state.loading = true
    return axios.post(`${process.env.VUE_APP_API_URL}/fiscal/${id}/correction-letter?${getCurrentCompanyQuery()}`, { correctionText })
      .then(res => {
        showSuccess('Carta de correção enfileirada com sucesso!')
        dispatch('get')
        return res.data
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  substitute: async ({ state, dispatch }, { id, reason }) => {
    state.loading = true
    return axios.post(`${process.env.VUE_APP_API_URL}/fiscal/${id}/substitute?${getCurrentCompanyQuery()}`, { reason })
      .then(async res => {
        showSuccess(res?.data?.message || 'Substituição iniciada com sucesso!')
        await dispatch('get')
        return res.data
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  inutilizeNumber: async ({ state }, payload) => {
    state.loading = true
    return axios.post(`${process.env.VUE_APP_API_URL}/fiscal/number-inutilization?${getCurrentCompanyQuery()}`, payload)
      .then(res => {
        showSuccess('Inutilização de numeração enfileirada com sucesso!')
        return res.data
      })
      .catch(showError)
      .finally(() => (state.loading = false))
  },

  consultNfe: async ({ state }, payload) => {
    state.loading = true
    try {
      const res = await axios.post(`${process.env.VUE_APP_API_URL}/fiscal/nfe/consult?${getCurrentCompanyQuery()}`, payload)
      showSuccess('Consulta da NFe enfileirada com sucesso!')
      return res.data
    } catch (error) {
      showError(error)
      throw error
    } finally {
      state.loading = false
    }
  },

  importXmls: async ({ state }, rows) => {
    const formData = new FormData()
    rows.forEach(row => formData.append('files', row.file))
    formData.append('bindings', JSON.stringify(rows.map((row, index) => ({
      index,
      requestId: row.requestId,
      nfseProvider: row.nfseProvider,
    }))))

    state.loading = true
    try {
      const response = await axios.post(
        `${process.env.VUE_APP_API_URL}/fiscal/import-xmls?${getCurrentCompanyQuery()}`,
        formData,
      )
      return response.data
    } finally {
      state.loading = false
    }
  },

  getOperationsByFiscalId: (context, id) => axios.get(`${process.env.VUE_APP_API_URL}/fiscal/${id}/operations?${getCurrentCompanyQuery()}`)
    .then(res => res.data)
    .catch(showError),

  getById: (context, id) => axios.get(`${process.env.VUE_APP_API_URL}/fiscal/${id}`).catch(showError),

  getByRequestId: (context, requestId) => axios.get(`${process.env.VUE_APP_API_URL}/fiscal/request/${requestId}`).catch(showError),

  generatePDF: async (context, { id }) => {
    state.loading = true
    try {
      const res = await axios.get(`${process.env.VUE_APP_API_URL}/fiscal/${id}/pdf?${getCurrentCompanyQuery()}`, {
        responseType: 'arraybuffer',
        headers: {
          Accept: 'application/pdf',
        },
      })
      const contentType = String(res.headers?.['content-type'] || '').toLowerCase()
      if (contentType && !contentType.includes('application/pdf')) {
        throw new Error('O servidor não retornou um PDF válido')
      }

      const bytes = new Uint8Array(res.data)
      const pdfHeader = String.fromCharCode(...bytes.slice(0, 4))
      if (pdfHeader !== '%PDF') {
        throw new Error('O arquivo retornado não parece ser um PDF válido')
      }

      const url = window.URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }))
      window.open(url, '_blank')
      setTimeout(() => window.URL.revokeObjectURL(url), 60000)
    } catch (e) {
      console.log(e)
      showError(e)
    } finally {
      state.loading = false
    }
  },
}

const getters = make.getters(state)

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
